import React from "react";
import { Link } from "react-router-dom";
import { FiChevronDown, FiKey } from "react-icons/fi";
import BuyElectricityCard from "../../components/landing/buy-card";
import RetrieveTokenModal from "../../components/modals/guest/retrieve-token-modal";
import PhoneMockup from "../../components/landing/phone-mockup";
import { StoreButtons } from "../../components/landing/footer";
import { faqs, features, stats, steps } from "../../constants/landing";

const container = "mx-auto w-[92%] max-w-[1200px]";

function Eyebrow({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-secondary-dark/15 px-3.5 py-1 text-[13px] font-semibold text-[#9a6f08]">
      {children}
    </span>
  );
}

function Hero() {
  const [meterId, setMeterId] = React.useState("");
  const [retrieveOpen, setRetrieveOpen] = React.useState(false);

  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_85%_20%,rgba(225,170,43,0.22),transparent),radial-gradient(50%_45%_at_5%_0%,rgba(1,13,85,0.10),transparent)]"
      />
      <div className={`${container} grid items-center gap-14 pb-20 pt-10 lg:grid-cols-[1.05fr_0.95fr] lg:pb-28 lg:pt-16`}>
        <div className="space-y-8">
          <Eyebrow>Central vending for communities</Eyebrow>
          <h1 className="text-[40px] font-extrabold leading-[1.05] tracking-tight text-primary-dark sm:text-5xl lg:text-[64px]">
            Buy electricity in{" "}
            <span className="bg-gradient-to-r from-[#E1AA2B] to-[#c4791a] bg-clip-text text-transparent">
              minutes
            </span>
            , not queues.
          </h1>
          <p className="max-w-[540px] text-lg leading-relaxed text-[#5b5f73]">
            Enter your meter ID, transfer the amount and get your token. Plus smart gate and visitor
            management that keeps your community secure.
          </p>

          <BuyElectricityCard onMeterChange={setMeterId} />

          <div className="grid w-full max-w-[520px] grid-cols-2 gap-3 sm:flex sm:max-w-none sm:flex-wrap">
            <Link
              to="/buy-meter"
              className="flex h-[52px] items-center justify-center whitespace-nowrap rounded-full bg-primary-dark px-3 text-[14px] sm:text-[15px] font-semibold text-white transition hover:bg-primary-light sm:px-6"
            >
              Get a meter
            </Link>
            <button
              type="button"
              onClick={() => setRetrieveOpen(true)}
              className="flex h-[52px] items-center justify-center gap-2 whitespace-nowrap rounded-full border border-secondary-dark/60 bg-secondary-light px-3 text-[14px] sm:text-[15px] font-semibold text-primary-dark transition hover:bg-secondary-dark/20 sm:px-6"
            >
              <FiKey />
              Retrieve token
            </button>
          </div>
        </div>

        <div className="relative flex justify-center">
          <div
            aria-hidden
            className="absolute inset-x-6 top-10 -z-10 h-[78%] rounded-[3rem] bg-gradient-to-br from-primary-light to-primary-dark opacity-95"
          />
          <PhoneMockup className="lg:translate-y-4" />
          <div className="absolute -left-4 top-6 hidden rounded-2xl bg-white px-4 py-3 shadow-xl sm:block lg:-left-14">
            <p className="text-xs text-[#6C6F7F]">Token delivered</p>
            <p className="text-base font-bold text-primary-dark">⚡ 42.6 kWh</p>
          </div>
          <div className="absolute -right-4 bottom-16 hidden rounded-2xl bg-white px-4 py-3 shadow-xl sm:block lg:-right-4">
            <p className="text-xs text-[#6C6F7F]">Wallet balance</p>
            <p className="text-base font-bold text-primary-dark">₦41,761</p>
          </div>
        </div>
      </div>

      {retrieveOpen && (
        <RetrieveTokenModal initialMeterId={meterId} onClose={() => setRetrieveOpen(false)} />
      )}
    </section>
  );
}

function Stats() {
  return (
    <section className={container}>
      <div className="grid gap-px overflow-hidden rounded-3xl bg-black/5 sm:grid-cols-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-white px-8 py-8 text-center">
            <p className="text-4xl font-extrabold text-primary-dark">{s.value}</p>
            <p className="mt-1 text-[#6C6F7F]">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className={`${container} py-24`}>
      <div className="mx-auto mb-14 max-w-[640px] space-y-4 text-center">
        <Eyebrow>Everything in one place</Eyebrow>
        <h2 className="text-3xl font-extrabold tracking-tight text-primary-dark sm:text-4xl">
          Powering communities, end to end
        </h2>
        <p className="text-lg text-[#5b5f73]">
          From vending to visitors, a single platform for residents and estate managers.
        </p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {features.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="group rounded-3xl border border-black/5 bg-[#FAFAFD] p-7 transition hover:-translate-y-1 hover:bg-white hover:shadow-[0_20px_40px_-20px_rgba(1,6,35,0.2)]"
          >
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-dark text-secondary-dark">
              <Icon className="text-lg" />
            </div>
            <h3 className="mb-2 text-xl font-bold text-primary-dark">{title}</h3>
            <p className="leading-relaxed text-[#5b5f73]">{body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="bg-primary-dark py-24 text-white">
      <div className={container}>
        <div className="mb-14 max-w-[560px] space-y-4">
          <span className="text-sm font-semibold uppercase tracking-wider text-secondary-dark">How it works</span>
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">Three steps to power</h2>
        </div>
        <ol className="grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <li key={s.title} className="rounded-3xl border border-white/10 bg-white/5 p-8">
              <span className="mb-6 flex h-11 w-11 items-center justify-center rounded-full bg-secondary-dark text-lg font-bold text-primary-dark">
                {i + 1}
              </span>
              <h3 className="mb-2 text-xl font-bold">{s.title}</h3>
              <p className="leading-relaxed text-white/70">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function AppSection() {
  return (
    <section className={`${container} grid items-center gap-14 py-24 lg:grid-cols-2`}>
      <div className="order-2 space-y-6 lg:order-1">
        <Eyebrow>The Prepaid Meter app</Eyebrow>
        <h2 className="text-3xl font-extrabold tracking-tight text-primary-dark sm:text-4xl">
          Your meter, wallet and access codes in your pocket
        </h2>
        <p className="max-w-[520px] text-lg leading-relaxed text-[#5b5f73]">
          Recharge your meter, pay bills, issue visitor access codes and watch your energy use, all
          from one simple dashboard.
        </p>
        <StoreButtons />
      </div>
      <div className="order-1 lg:order-2">
        <div className="relative mx-auto max-w-[420px] rounded-[3rem] bg-secondary-light px-6 pt-12">
          <PhoneMockup className="translate-y-6" />
          <div className="h-10" />
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const [openIdx, setOpenIdx] = React.useState(0);
  return (
    <section className="bg-[#FAFAFD] py-24">
      <div className="mx-auto w-[92%] max-w-[820px]">
        <h2 className="mb-10 text-center text-3xl font-extrabold tracking-tight text-primary-dark sm:text-4xl">
          Frequently asked questions
        </h2>
        <div className="space-y-3">
          {faqs.map((f, i) => {
            const isOpen = openIdx === i;
            return (
              <div key={f.q} className="rounded-2xl border border-black/5 bg-white">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpenIdx(isOpen ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="text-[17px] font-semibold text-primary-dark">{f.q}</span>
                  <FiChevronDown className={`shrink-0 transition ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && <p className="px-6 pb-6 leading-relaxed text-[#5b5f73]">{f.a}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Cta() {
  return (
    <section className={`${container} py-24`}>
      <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-primary-light to-primary-dark px-8 py-14 text-center text-white sm:px-16">
        <div aria-hidden className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-secondary-dark/25 blur-3xl" />
        <h2 className="relative text-3xl font-extrabold tracking-tight sm:text-4xl">
          Bring smarter vending to your community
        </h2>
        <p className="relative mx-auto mt-4 max-w-[540px] text-lg text-white/75">
          Get a meter for your property and see how central vending, gate pass and visitor management fit your estate.
        </p>
        <div className="relative mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/buy-meter" className="rounded-full border border-white/30 px-7 py-3.5 font-semibold text-white transition hover:bg-white/10">
            Get a meter
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function HomepageWrapper() {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <Hero />
      <Stats />
      <Features />
      <HowItWorks />
      <AppSection />
      <Faq />
      <Cta />
    </>
  );
}
